import React from 'react';
import { Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle2, Mail, X, BookHeart } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import GoogleIcon from './GoogleIcon';

interface LoginFormProps {
  formData: { email: string; password: string };
  errors: Record<string, string>;
  loading: boolean;
  showPassword: boolean;
  emailInputRef: React.RefObject<HTMLInputElement>;
  onFieldChange: (field: string, value: string) => void;
  onTogglePassword: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitchMode: (mode: 'login' | 'register' | 'forgot-password') => void;
  onGoogleSignIn: (code: string) => void;
  onClose?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  formData,
  errors,
  loading,
  showPassword,
  emailInputRef,
  onFieldChange,
  onTogglePassword,
  onSubmit,
  onSwitchMode,
  onGoogleSignIn,
  onClose
}) => {
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    onSuccess: (response) => onGoogleSignIn(response.code),
    onError: () => onGoogleSignIn(''),
  });

  const inputBase =
    'w-full border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 placeholder:text-stone-400 transition-all duration-200';
  const inputSizing = 'h-10 text-sm px-3 lg:h-11 lg:px-3.5';
  const inputClass = (hasError: boolean) =>
    `${inputBase} ${inputSizing} ${hasError ? 'border-rose-500' : 'border-stone-200'}`;

  return (
    <form onSubmit={onSubmit} className="h-full w-full flex flex-col lg:flex-row min-h-0">
      {/* MOBILE: compact gradient header */}
      <div className="lg:hidden warm-panel relative pt-4 pb-3 px-5 shrink-0">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="absolute right-3 top-3 flex items-center justify-center text-stone-500 rounded-full bg-white/60 hover:bg-white/80 transition-colors disabled:opacity-50 touch-manipulation"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        )}
        <h2
          id="auth-modal-title"
          className="font-display text-[22px] leading-tight text-stone-900 pr-10"
        >
          Hola de nuevo.
        </h2>
        <p className="text-[12px] text-stone-600 mt-0.5">
          Continúa escribiendo tu historia
        </p>
      </div>

      {/* DESKTOP: split-panel left — brand hero */}
      <div className="hidden lg:flex lg:w-1/2 warm-panel relative overflow-hidden flex-col justify-center px-14">
        <div className="w-10 h-10 bg-rose-400 rounded-2xl flex items-center justify-center text-white shadow-lg mb-4">
          <BookHeart size={20} />
        </div>
        <h1 className="font-display text-[42px] leading-[1.05] text-stone-900">
          Hola de nuevo.
        </h1>
        <p className="text-sm text-stone-600 mt-4 max-w-[340px] leading-relaxed">
          Continúa escribiendo las historias de tu familia.
        </p>
        <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-rose-200/40 blur-3xl pointer-events-none" />
      </div>

      {/* FORM REGION */}
      <div className="warm-panel lg:w-1/2 lg:bg-[#FEFCFA] flex-1 flex flex-col min-h-0 lg:px-12 lg:py-10 relative">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="hidden lg:flex items-center justify-center absolute top-10 right-10 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded-full transition-colors disabled:opacity-50 touch-manipulation"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        )}

        <div className="hidden lg:block mb-5 shrink-0">
          <h2 className="text-xl font-semibold text-stone-900 mb-0.5">Iniciar sesión</h2>
          <p className="text-xs text-stone-500">Accede a tu cuenta</p>
        </div>

        {/* Mobile card wrapper / desktop plain */}
        <div className="flex-1 flex flex-col min-h-0 px-3 pb-3 lg:px-0 lg:pb-0">
          <div className="flex-1 flex flex-col min-h-0 bg-white lg:bg-transparent rounded-2xl lg:rounded-none border lg:border-0 border-stone-200 shadow-sm lg:shadow-none p-4 lg:p-0">

            <div className="space-y-3 flex-1 min-h-0 overflow-y-auto p-0.5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-[13px] font-medium text-stone-700 mb-1">
                  Email
                </label>
                <input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => onFieldChange('email', e.target.value)}
                  className={inputClass(Boolean(errors.email))}
                  placeholder="tu@email.com"
                  required
                  autoComplete="email"
                  inputMode="email"
                />
                {errors.email && (
                  <p className="mt-1 text-[12px] text-rose-600 flex items-center" role="alert">
                    <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password + forgot link */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-[13px] font-medium text-stone-700">
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={() => onSwitchMode('forgot-password')}
                    className="text-[12px] lg:text-xs text-rose-500 hover:text-rose-600 transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => onFieldChange('password', e.target.value)}
                    className={`${inputClass(Boolean(errors.password))} pr-10`}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={onTogglePassword}
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors touch-manipulation p-1 !min-h-0 !min-w-0"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-[12px] text-rose-600 flex items-center" role="alert">
                    <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Submit error / success */}
              {errors.submit && (
                <div
                  className={`p-3 rounded-xl ${
                    errors.submit.startsWith('success:')
                      ? 'bg-green-50 text-green-800'
                      : errors.submit.startsWith('emailNotConfirmed:')
                      ? 'bg-amber-50 border-2 border-amber-200 text-amber-900'
                      : 'bg-rose-50 text-rose-800'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {errors.submit.startsWith('success:') ? (
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    ) : errors.submit.startsWith('emailNotConfirmed:') ? (
                      <Mail className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-xs lg:text-sm leading-snug">
                      {errors.submit.replace('success:', '').replace('emailNotConfirmed:', '')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="pt-3 space-y-2 shrink-0">
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 shadow-lg shadow-rose-500/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/60 border-t-white animate-spin" />
                    Iniciar sesión
                  </span>
                ) : (
                  <>
                    Iniciar sesión <ArrowRight size={15} />
                  </>
                )}
              </button>

              <div className="flex items-center gap-3 py-0.5">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-xs text-stone-400">o</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>

              <button
                type="button"
                onClick={() => googleLogin()}
                disabled={loading}
                className="w-full h-10 flex items-center justify-center gap-2 border-2 border-stone-200 rounded-full hover:border-stone-300 hover:bg-stone-50 shadow-sm text-sm font-medium text-stone-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                <GoogleIcon size={18} />
                <span>Continuar con Google</span>
              </button>
            </div>
          </div>

          <p className="text-center text-[12px] lg:text-xs text-stone-600 lg:text-stone-500 pt-2 lg:pt-3 shrink-0">
            ¿No tienes cuenta?{' '}
            <button
              type="button"
              onClick={() => onSwitchMode('register')}
              className="text-rose-600 hover:text-rose-700 font-semibold"
            >
              Regístrate
            </button>
          </p>
        </div>
      </div>
    </form>
  );
};

export default LoginForm;
